import classNames from 'classnames'
import _ from 'lodash'
import React from 'react'
import { Classes, Icon } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'

import apis from 'browser/app/models/apis'
import { Button } from 'browser/components/atomic-elements/atoms/button/button'
import { LoadingSpinner } from 'browser/components/atomic-elements/atoms/loading-spinner/loading-spinner'
import { Popover } from 'browser/components/atomic-elements/atoms/popover/popover'
import { IComponentsContext } from 'browser/contexts/components/components-context'
import ComponentsMap from 'browser/components'
import { UI2EntityPreviewCard } from 'browser/components/ui2/display/ui2-entity-preview-card/ui2-entity-preview-card'
import { ViewRenderer } from 'shared-libs/components/view/renderer'
import { Entity } from 'shared-libs/models/entity'
import { Query } from 'shared-libs/models/query'
import { UI2EntitySearchFieldProps } from 'shared-libs/components/ui2/fields/ui2-entity-search-field/ui2-entity-search-field-props'
import { ResolvedSchemaPaths } from 'shared-libs/components/ui2/fields/ui2-entity-search-field/entity-search-utils'

import './_entity-search-submit-results.scss'

const NUM_RESULTS = 20
const DEFAULT_SEARCH_ORDER = [{ path: 'creationDate', type: 'descending' }]

interface IEntitySearchSubmitResultsProps {
  fieldProps: UI2EntitySearchFieldProps
  componentsContext: IComponentsContext
  entitySchema: any
  entityDisplayName: string
  schemaPaths: ResolvedSchemaPaths
  searchFilter: string
  onCommitSelection: (payload: { entity: any; searchFilter: string }) => void
  onCommitRawValue?: (searchFilter: string) => void
  onSubmitSearch?: (hasResults: boolean) => void
  onClose?: () => void
  /** Width (px) forwarded from the field wrapper so the popover renders at the same width. */
  width?: number
}

interface IEntitySearchSubmitResultsState {
  entities: Entity[]
  isSearching: boolean
  selectedItem?: Entity
}

export class EntitySearchSubmitResults extends React.Component<
  IEntitySearchSubmitResultsProps,
  IEntitySearchSubmitResultsState
> {
  private collection: any

  constructor(props: IEntitySearchSubmitResultsProps) {
    super(props)
    this.state = { entities: [], isSearching: false, selectedItem: undefined }
  }

  public componentDidMount() {
    this.runQuery(this.props.searchFilter)
  }

  public componentDidUpdate(prevProps: IEntitySearchSubmitResultsProps) {
    if (prevProps.searchFilter !== this.props.searchFilter) {
      this.runQuery(this.props.searchFilter)
    }
  }

  public componentWillUnmount() {
    this.collection?.cancel()
    this.collection?.dispose()
  }

  public render() {
    const { isSearching, entities } = this.state
    const { width, onClose } = this.props
    return (
      <Popover className='ui2-entitySearchSubmitResults'>
        <div
          className='ui2-entitySearchSubmitResults-content'
          style={width ? { width, maxWidth: width } : undefined}
        >
          <Button
            className={classNames(Classes.MINIMAL, 'ui2-entitySearchSubmitResults-close')}
            onClick={onClose}
          >
            <Icon icon={IconNames.CROSS} />
          </Button>
          {isSearching ? (
            <div className='ui2-entitySearchSubmitResults-loading'>
              <LoadingSpinner />
            </div>
          ) : _.isEmpty(entities) ? (
            <>
              {this.renderEmptyResultState()}
              {this.renderRawSubmitOption()}
            </>
          ) : (
            <>
              <div className='ui2-entitySearchSubmitResults-header'>
                {`Results for "${this.props.searchFilter}" (click to select)`}
              </div>
              {_.map(entities, (entity, index) => this.renderResultCard(entity, index))}
              {this.renderRawSubmitOption()}
            </>
          )}
        </div>
      </Popover>
    )
  }

  private renderRawSubmitOption = () => {
    const { fieldProps, searchFilter } = this.props
    if (!fieldProps.rawQueryPath || _.isEmpty(_.trim(searchFilter))) return null
    const trimmed = _.trim(searchFilter)
    const label = fieldProps.rawSubmitLabel
      ? fieldProps.rawSubmitLabel.replace('{searchFilter}', trimmed)
      : `Not listed? Use "${trimmed}" instead`
    return (
      <div
        className='ui2-entitySearchSubmitResults-rawItem'
        onClick={() => this.handleRawSubmit(trimmed)}
      >
        {label}
      </div>
    )
  }

  private renderEmptyResultState = () => {
    const { searchEmptyResults } = this.props.schemaPaths
    if (searchEmptyResults && _.get(this.props.entitySchema, searchEmptyResults)) {
      return (
        <ViewRenderer
          schema={_.get(this.props.entitySchema, searchEmptyResults)}
          state={this.getRendererState()}
          componentsMap={ComponentsMap}
          api={apis}
        />
      )
    }
    return (
      <div className='ui2-entitySearchSubmitResults-empty'>
        {`No results found for "${this.props.searchFilter}"`}
      </div>
    )
  }

  private renderResultCard = (entity: Entity, index: number) => {
    const { selectedItem } = this.state
    const { entitySearchResultCard } = this.props.schemaPaths
    const { fieldProps } = this.props
    const isSelected = selectedItem?.uniqueId === entity?.uniqueId

    const schema = entitySearchResultCard && _.get(this.props.entitySchema, entitySearchResultCard)
    let content: React.ReactNode
    if (
      schema &&
      (schema.type === 'ui:form:entityPreviewCard' || schema.type === 'ui:entityPreviewCard')
    ) {
      const { type: _type, ...cardPropsFromSchema } = schema
      content = <UI2EntityPreviewCard entity={entity} {...cardPropsFromSchema} isDisabled={true} showTypeStripe={false} />
    } else {
      if (schema) {
        // eslint-disable-next-line no-console
        console.warn(
          `[UI2EntitySearchField] entitySearchResultCardSchemaPath "${entitySearchResultCard}" is ` +
            `not a top-level ui:form:entityPreviewCard. Falling back to the displayName card.`
        )
      }
      const optionValuePath = fieldProps.optionValuePath ?? 'displayName'
      content = (
        <UI2EntityPreviewCard
          entity={entity}
          title={_.get(entity, optionValuePath) ?? entity.displayName}
          isDisabled={true}
          showTypeStripe={false}
        />
      )
    }

    return (
      <div
        key={entity?.uniqueId || index}
        className={classNames('ui2-entitySearchSubmitResults-card', {
          'ui2-entitySearchSubmitResults-card--selected': isSelected,
        })}
        onClick={() => this.handleSelectEntity(entity)}
      >
        {content}
      </div>
    )
  }

  private handleRawSubmit = (trimmed: string) => {
    this.props.onCommitRawValue?.(trimmed)
    this.props.onClose?.()
  }

  private handleSelectEntity = async (entity: Entity) => {
    this.setState({ selectedItem: entity })
    const refreshed = await this.refreshEntity(entity)
    this.props.onCommitSelection({ entity: refreshed, searchFilter: this.props.searchFilter })
    this.props.onClose?.()
  }

  private runQuery = (searchFilter: string) => {
    if (_.isEmpty(searchFilter)) {
      this.setState({ entities: [], isSearching: false })
      return
    }
    this.queryEntities(searchFilter)
  }

  private queryEntities = async (searchFilter: string) => {
    const { query = {}, transformSearchFilterFn, transformResultsFn } = this.props.fieldProps
    const { entityType, queryPath, queryPaths, queryType } = query
    this.setState({ isSearching: true, selectedItem: undefined })

    if (!entityType) {
      this.setState({ entities: [], isSearching: false })
      return
    }

    const searchQuery = transformSearchFilterFn ? transformSearchFilterFn(searchFilter) : searchFilter

    const queryBuilder = new Query(apis)
      .setEntityType(entityType)
      .setQueryType(queryType)
      .setComputations(query?.computations)
      .setGroups(query?.groups)
      .setFilters(query?.filters)
      .setMetadata(query?.metadata)
      .setSize(NUM_RESULTS)
      .setOrders(query?.orders || DEFAULT_SEARCH_ORDER)
      .setQuery(searchQuery)

    if (queryPaths) {
      queryBuilder.setQueryPaths(queryPaths)
    } else {
      queryBuilder.setQueryPath(queryPath)
    }

    this.collection?.cancel()
    this.collection?.dispose()
    this.collection = queryBuilder.getCollection()

    try {
      let entities = await this.collection.find()
      if (transformResultsFn) {
        entities = await Promise.resolve(transformResultsFn({ entities, searchQuery }))
      }
      this.props.onSubmitSearch?.(!_.isEmpty(entities))
      this.setState({ entities })
    } catch (e) {
      if (this.isCancellationError(e)) return
      // eslint-disable-next-line no-console
      console.error('[UI2EntitySearchField] submit query failed', { error: e, searchFilter })
      this.props.onSubmitSearch?.(false)
      this.setState({ entities: [] })
    } finally {
      this.collection = undefined
      this.setState({ isSearching: false })
    }
  }

  private refreshEntity = async (entity: Entity) => {
    if (_.isNil(entity?.uniqueId)) return entity
    try {
      return await apis.getStore().findRecord(entity.uniqueId)
    } catch {
      return entity
    }
  }

  private isCancellationError = (error: any) =>
    error?.name === 'CancellationError' ||
    error?.message?.includes('canceled') ||
    error?.message?.includes('cancelled') ||
    error?.message?.includes('aborted')

  private getRendererState = () => ({
    ...this.props.componentsContext,
    ...this.props.fieldProps,
    entities: this.state.entities,
    searchFilter: this.props.searchFilter,
    entityDisplayName: this.props.entityDisplayName,
  })
}
